import React, { PropsWithChildren, createContext, useCallback, useEffect, useState } from 'react';

import { UseQueryOptions, useQuery } from '@tanstack/react-query';

import { getCsrfToken, login, logout } from '~/controllers/auth';
import { setNonce } from '~/store/auth';

export enum AuthStatus {
  AUTHENTICATED,
  NOT_AUTHENTICATED,
  UNKNOWN
}

export interface UserAuthData {
  authStatus: AuthStatus;
  nonce: string;
}

interface UserAuthOutput {
  userAuth: UserAuthData;
  logout: () => void;
}

const commonQueryOpts: UseQueryOptions = {
  retry: 0,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
};

const csrfQueryOpts: UseQueryOptions = {
  ...commonQueryOpts,
  staleTime: Infinity,
};

const loginQueryOpts: UseQueryOptions = {
  ...commonQueryOpts,
  cacheTime: 0,
  staleTime: 0,
};

const DEFAULT_NONCE = '';

const UserAuthContext = createContext<UserAuthOutput>({
  userAuth: {
    authStatus: AuthStatus.UNKNOWN,
    nonce: DEFAULT_NONCE,
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  logout: () => {},
});

const UserAuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [userAuth, setUserAuth] = useState<UserAuthData>({
    authStatus: AuthStatus.UNKNOWN,
    nonce: DEFAULT_NONCE,
  });

  const [hasToLogin, setHasToLogin] = useState(false);

  const { data: csrfData, error: csrfError } = useQuery(['csrf'], getCsrfToken, {
    ...csrfQueryOpts,
  });

  useQuery(['login'], login, {
    ...loginQueryOpts,
    enabled: hasToLogin,
  });

  const setLoginData = useCallback((nonce: string) => {
    setUserAuth({
      authStatus: AuthStatus.AUTHENTICATED,
      nonce,
    });
    setNonce(nonce);
  }, []);

  const removeLoginData = useCallback(() => {
    setUserAuth({
      authStatus: AuthStatus.NOT_AUTHENTICATED,
      nonce: DEFAULT_NONCE,
    });
    setHasToLogin(true);
  }, []);

  useEffect(() => {
    window.addEventListener('logout', () => removeLoginData);

    return () => {
      window.removeEventListener('logout', removeLoginData);
    }
  }, [removeLoginData]);

  useEffect(() => {
    if (csrfData) {
      setLoginData(csrfData as string);
    }
  }, [csrfData, setLoginData]);

  useEffect(() => {
    if (csrfError) {
      removeLoginData();
    }
  }, [csrfError, removeLoginData]);

  const logoutCallback = useCallback(async() => {
    await logout();
    removeLoginData();
  }, [removeLoginData]);

  const value: UserAuthOutput = {
    userAuth,
    logout: logoutCallback,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  )
};

export { UserAuthContext, UserAuthProvider };
